import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { BrandingSettingsService } from './proxy/branding-settings.service';
import { BrandingSettingsDto } from './proxy/branding-settings.model';
import { ToasterService } from '@abp/ng.theme.shared';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { BrandingSettingsHelperService } from '../../shared/services/branding-settings-helper.service';

@Component({
  selector: 'cai-branding-settings',
  templateUrl: './branding-settings.component.html',
  styleUrls: ['./branding-settings.component.scss'],
  standalone: false,
})
export class BrandingSettingsComponent implements OnInit {
  form: UntypedFormGroup;
  isLoading: boolean = false;
  isSaving: boolean = false;

  // Logo state
  logoFile: File | null = null;
  logoPreviewUrl: SafeUrl | null = null;
  currentLogoDataUrl: string | null = null;
  logoUploaded: boolean = false;

  // Favicon state
  faviconFile: File | null = null;
  faviconPreviewUrl: SafeUrl | null = null;
  currentFaviconDataUrl: string | null = null;
  faviconUploaded: boolean = false;

  // Validation
  readonly footerTextMaxLength = 200;
  readonly aiDisclaimerMaxLength = 2000;
  readonly logoMaxSize = 5 * 1024 * 1024; // 5MB
  readonly faviconMaxSize = 1 * 1024 * 1024; // 1MB

  constructor(
    private brandingSettingsService: BrandingSettingsService,
    private fb: UntypedFormBuilder,
    private toaster: ToasterService,
    private sanitizer: DomSanitizer,
    private brandingSettingsHelper: BrandingSettingsHelperService,
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadSettings();
  }

  buildForm(): void {
    this.form = this.fb.group({
      footerText: ['', [Validators.required, Validators.maxLength(this.footerTextMaxLength)]],
      aiUsageDisclaimer: [
        '',
        [Validators.required, Validators.maxLength(this.aiDisclaimerMaxLength)],
      ],
    });

    // Normalize line endings to \n
    this.form.get('footerText')?.valueChanges.subscribe((value) => {
      if (value && value.includes('\r\n')) {
        const normalized = value.replace(/\r\n/g, '\n');
        this.form.get('footerText')?.setValue(normalized, { emitEvent: false });
      }
    });

    this.form.get('aiUsageDisclaimer')?.valueChanges.subscribe((value) => {
      if (value && value.includes('\r\n')) {
        const normalized = value.replace(/\r\n/g, '\n');
        this.form.get('aiUsageDisclaimer')?.setValue(normalized, { emitEvent: false });
      }
    });
  }

  loadSettings(): void {
    this.isLoading = true;
    this.brandingSettingsService.get().subscribe({
      next: (settings: BrandingSettingsDto) => {
        this.form.patchValue({
          footerText: settings.footerText || '',
          aiUsageDisclaimer: settings.aiUsageDisclaimer || '',
        });

        if (settings.logoBase64 && settings.logoContentType) {
          this.currentLogoDataUrl = this.brandingSettingsHelper.createDataUrl(
            settings.logoBase64,
            settings.logoContentType,
          );
          this.logoUploaded = true;
        }

        if (settings.faviconBase64 && settings.faviconContentType) {
          this.currentFaviconDataUrl = this.brandingSettingsHelper.createDataUrl(
            settings.faviconBase64,
            settings.faviconContentType,
          );
          this.faviconUploaded = true;
        }

        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading branding settings:', err);
        this.isLoading = false;
      },
    });
  }

  // Logo handlers
  onLogoFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate file type
      const validTypes = ['image/png', 'image/svg+xml', 'image/jpeg', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        this.toaster.error('::BrandingSettings:InvalidLogoFormat');
        return;
      }

      // Validate file size
      if (file.size > this.logoMaxSize) {
        this.toaster.error('::BrandingSettings:LogoSizeExceeded');
        return;
      }

      this.logoFile = file;
      this.generatePreview(file, 'logo');
      this.logoUploaded = true;

      // Clear the input value to allow selecting the same file again
      input.value = '';
    }
  }

  onLogoRemove(): void {
    this.logoFile = null;
    this.logoPreviewUrl = null;
    this.currentLogoDataUrl = null;
    this.logoUploaded = false;
  }

  triggerLogoInput(): void {
    document.getElementById('logoInput')?.click();
  }

  // Favicon handlers
  onFaviconFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate file type
      const validTypes = ['image/x-icon', 'image/png', 'image/vnd.microsoft.icon'];
      if (!validTypes.includes(file.type) && !file.name.endsWith('.ico')) {
        this.toaster.error('::BrandingSettings:InvalidFaviconFormat');
        return;
      }

      // Validate file size
      if (file.size > this.faviconMaxSize) {
        this.toaster.error('::BrandingSettings:FaviconSizeExceeded');
        return;
      }

      this.faviconFile = file;
      this.generatePreview(file, 'favicon');
      this.faviconUploaded = true;

      // Clear the input value
      input.value = '';
    }
  }

  onFaviconRemove(): void {
    this.faviconFile = null;
    this.faviconPreviewUrl = null;
    this.currentFaviconDataUrl = null;
    this.faviconUploaded = false;
  }

  triggerFaviconInput(): void {
    document.getElementById('faviconInput')?.click();
  }

  // Preview generation
  private generatePreview(file: File, type: 'logo' | 'favicon'): void {
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const url = e.target?.result as string;
      const safeUrl = this.sanitizer.sanitize(1, url);

      if (type === 'logo') {
        this.logoPreviewUrl = safeUrl;
      } else {
        this.faviconPreviewUrl = safeUrl;
      }
    };
    reader.readAsDataURL(file);
  }

  // Form submission
  submitForm(): void {
    if (!this.canSave) {
      return;
    }

    this.isSaving = true;

    const formData = new FormData();

    if (this.logoFile) {
      formData.append('logoFile', this.logoFile);
    }

    if (this.faviconFile) {
      formData.append('faviconFile', this.faviconFile);
    }

    formData.append('footerText', this.form.get('footerText')?.value || '');
    formData.append('aiUsageDisclaimer', this.form.get('aiUsageDisclaimer')?.value || '');

    this.brandingSettingsService.update(formData).subscribe({
      next: (response: BrandingSettingsDto) => {
        this.isSaving = false;
        this.toaster.success('AbpUi::SavedSuccessfully');

        // Update current data URLs from response
        if (response.logoBase64 && response.logoContentType) {
          this.currentLogoDataUrl = this.brandingSettingsHelper.createDataUrl(
            response.logoBase64,
            response.logoContentType,
          );
          this.logoFile = null;
          this.logoPreviewUrl = null;
        }

        if (response.faviconBase64 && response.faviconContentType) {
          this.currentFaviconDataUrl = this.brandingSettingsHelper.createDataUrl(
            response.faviconBase64,
            response.faviconContentType,
          );
          this.faviconFile = null;
          this.faviconPreviewUrl = null;
        }

        // Update favicon in browser
        if (this.currentFaviconDataUrl) {
          this.brandingSettingsHelper.updateBrowserFavicon(this.currentFaviconDataUrl);
        }

        // Emit event for other components to refresh
        window.dispatchEvent(new CustomEvent('branding-updated', { detail: response }));
      },
      error: (err) => {
        console.error('Error saving branding settings:', err);
        this.isSaving = false;
      },
    });
  }

  // Validation
  get canSave(): boolean {
    return (
      this.form.valid &&
      this.hasValidLogo() &&
      this.hasValidFavicon() &&
      !this.isSaving &&
      this.footerTextLength <= this.footerTextMaxLength &&
      this.aiDisclaimerLength <= this.aiDisclaimerMaxLength
    );
  }

  private hasValidLogo(): boolean {
    return this.logoUploaded && (this.logoFile !== null || this.currentLogoDataUrl !== null);
  }

  private hasValidFavicon(): boolean {
    return (
      this.faviconUploaded && (this.faviconFile !== null || this.currentFaviconDataUrl !== null)
    );
  }

  get footerTextLength(): number {
    const value = this.form.get('footerText')?.value || '';
    return value.length;
  }

  get aiDisclaimerLength(): number {
    const value = this.form.get('aiUsageDisclaimer')?.value || '';
    return value.length;
  }

  // Logo display helpers
  get displayLogoUrl(): string | SafeUrl | null {
    if (this.logoPreviewUrl) {
      return this.logoPreviewUrl;
    }
    return this.currentLogoDataUrl;
  }

  get displayLogoFileName(): string {
    if (this.logoFile) {
      return this.logoFile.name;
    }
    return 'logo';
  }

  // Favicon display helpers
  get displayFaviconUrl(): string | SafeUrl | null {
    if (this.faviconPreviewUrl) {
      return this.faviconPreviewUrl;
    }
    return this.currentFaviconDataUrl;
  }

  get displayFaviconFileName(): string {
    if (this.faviconFile) {
      return this.faviconFile.name;
    }
    return 'favicon';
  }
}
